import { useState, useEffect, useRef, useCallback } from 'react'
import { X, Send, Bot, Loader2, Mic, MicOff, Volume2, VolumeX, Sparkles } from 'lucide-react'
import { useTranslation } from 'react-i18next'

// ------------------------------------------------------------------
// Types
// ------------------------------------------------------------------

interface ChatMessage {
  role: 'bot' | 'user'
  text: string
  /** When true, the message is followed by a "show all products" action button */
  showClearAction?: boolean
}

export interface ShopAssistantSearchResult {
  total: number
  keywords: string[]
}

interface Props {
  /** Runs the AI search and applies the result to the product grid. */
  onSearch: (query: string) => Promise<ShopAssistantSearchResult>
  /** Clears the AI filter and restores the full catalog. */
  onClear: () => void
}

// Browser SpeechRecognition types (not in all TypeScript DOM libs).
// Same declarations as TicketChatbot — global interfaces merge.
interface SpeechRecognitionEvent extends Event {
  readonly resultIndex: number
  readonly results: SpeechRecognitionResultList
}

interface SpeechRecognitionInstance extends EventTarget {
  continuous: boolean
  interimResults: boolean
  lang: string
  maxAlternatives: number
  start(): void
  stop(): void
  abort(): void
  onstart: (() => void) | null
  onresult: ((ev: SpeechRecognitionEvent) => void) | null
  onerror: ((ev: Event) => void) | null
  onend: (() => void) | null
}

declare global {
  interface Window {
    SpeechRecognition: new () => SpeechRecognitionInstance
    webkitSpeechRecognition: new () => SpeechRecognitionInstance
  }
}

// ------------------------------------------------------------------
// Component
// ------------------------------------------------------------------

/**
 * Floating AI shop assistant (chatbot + voice bot).
 * The user describes what they are looking for (typed or spoken); the query is
 * interpreted by AI server-side and the product grid is filtered accordingly.
 */
export default function ShopAssistant({ onSearch, onClear }: Props) {
  const { t, i18n } = useTranslation()

  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [inputValue, setInputValue] = useState('')
  const [searching, setSearching] = useState(false)

  // Voice state
  const [voiceSupported, setVoiceSupported] = useState(false)
  const [voiceMode, setVoiceMode] = useState(false)
  const [isListening, setIsListening] = useState(false)

  const voiceModeRef = useRef(false)
  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null)
  const pendingSubmitRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const handleSendRef = useRef<(text?: string) => void>(() => {})
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const API = window.SpeechRecognition || window.webkitSpeechRecognition
    setVoiceSupported(!!(API && window.speechSynthesis))
  }, [])

  useEffect(() => {
    voiceModeRef.current = voiceMode
  }, [voiceMode])

  // Auto-scroll to the latest message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, searching])

  // Welcome message on first open
  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: 'bot', text: t('shopAssistant.welcome') }])
    }
    if (open) inputRef.current?.focus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // ── TTS ───────────────────────────────────────────────────────────
  const speak = useCallback(
    (text: string) => {
      if (!voiceModeRef.current || !window.speechSynthesis) return
      window.speechSynthesis.cancel()
      const utt = new SpeechSynthesisUtterance(text)
      utt.lang = i18n.language === 'fr' ? 'fr-FR' : 'en-US'
      window.speechSynthesis.speak(utt)
    },
    [i18n.language]
  )

  // ── STT ───────────────────────────────────────────────────────────
  const stopListening = useCallback(() => {
    if (pendingSubmitRef.current) {
      clearTimeout(pendingSubmitRef.current)
      pendingSubmitRef.current = null
    }
    try {
      recognitionRef.current?.stop()
    } catch {
      /* ignore */
    }
    recognitionRef.current = null
    setIsListening(false)
  }, [])

  const startListening = useCallback(() => {
    const API = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!API) return
    try {
      recognitionRef.current?.stop()
    } catch {
      /* ignore */
    }
    window.speechSynthesis?.cancel()
    const recognition = new API()
    recognition.lang = i18n.language === 'fr' ? 'fr-FR' : 'en-US'
    recognition.interimResults = true
    recognition.continuous = false
    recognition.maxAlternatives = 1
    let finalTranscript = ''
    recognition.onstart = () => setIsListening(true)
    recognition.onresult = (ev: SpeechRecognitionEvent) => {
      let interim = ''
      finalTranscript = ''
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        if (ev.results[i].isFinal) finalTranscript += ev.results[i][0].transcript
        else interim += ev.results[i][0].transcript
      }
      setInputValue(finalTranscript || interim)
    }
    recognition.onend = () => {
      setIsListening(false)
      recognitionRef.current = null
      if (finalTranscript.trim()) {
        // Short delay so the user sees the final transcript before submit
        pendingSubmitRef.current = setTimeout(
          () => handleSendRef.current(finalTranscript.trim()),
          600
        )
      }
    }
    recognition.onerror = () => {
      setIsListening(false)
      recognitionRef.current = null
    }
    recognitionRef.current = recognition
    recognition.start()
  }, [i18n.language])

  const toggleVoiceMode = useCallback(() => {
    const next = !voiceModeRef.current
    setVoiceMode(next)
    if (next) {
      startListening()
    } else {
      window.speechSynthesis?.cancel()
      stopListening()
    }
  }, [startListening, stopListening])

  // Cleanup on unmount
  useEffect(
    () => () => {
      window.speechSynthesis?.cancel()
      stopListening()
    },
    [stopListening]
  )

  // ── Search ────────────────────────────────────────────────────────
  const handleSend = useCallback(
    async (text?: string) => {
      const query = (text ?? inputValue).trim()
      if (!query || searching) return
      setInputValue('')
      setMessages((prev) => [...prev, { role: 'user', text: query }])
      setSearching(true)
      try {
        const result = await onSearch(query)
        const reply =
          result.total > 0
            ? t('shopAssistant.found', { count: result.total })
            : t('shopAssistant.notFound')
        setMessages((prev) => [...prev, { role: 'bot', text: reply, showClearAction: true }])
        speak(reply)
      } catch {
        const reply = t('shopAssistant.error')
        setMessages((prev) => [...prev, { role: 'bot', text: reply }])
        speak(reply)
      } finally {
        setSearching(false)
        // In voice mode, listen again for a follow-up query
        if (voiceModeRef.current) startListening()
      }
    },
    [inputValue, searching, onSearch, speak, startListening, t]
  )

  useEffect(() => {
    handleSendRef.current = handleSend
  }, [handleSend])

  const handleClear = () => {
    onClear()
    setMessages((prev) => [...prev, { role: 'bot', text: t('shopAssistant.cleared') }])
  }

  const handleClose = () => {
    setOpen(false)
    setVoiceMode(false)
    window.speechSynthesis?.cancel()
    stopListening()
  }

  // ── Render ────────────────────────────────────────────────────────
  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-40 flex items-center gap-2 px-4 py-3 bg-primary-600 text-white rounded-full shadow-lg hover:bg-primary-700 hover:shadow-xl transition-all"
        title={t('shopAssistant.buttonTitle')}
      >
        <Sparkles className="w-5 h-5" />
        <span className="hidden sm:inline font-medium text-sm">{t('shopAssistant.button')}</span>
      </button>
    )
  }

  return (
    <div className="fixed bottom-6 right-6 z-40 w-[calc(100vw-3rem)] max-w-sm bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-primary-600 text-white">
        <div className="flex items-center gap-2 min-w-0">
          <Bot className="w-5 h-5 flex-shrink-0" />
          <div className="min-w-0">
            <p className="font-semibold text-sm truncate">{t('shopAssistant.title')}</p>
            <p className="text-xs text-primary-100 truncate">
              {isListening ? t('shopAssistant.listening') : t('shopAssistant.subtitle')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {voiceSupported && (
            <button
              onClick={toggleVoiceMode}
              className={`p-2 rounded-lg transition-colors ${
                voiceMode ? 'bg-white/25' : 'hover:bg-white/15'
              }`}
              title={voiceMode ? t('shopAssistant.voiceOff') : t('shopAssistant.voiceOn')}
            >
              {voiceMode ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </button>
          )}
          <button
            onClick={handleClose}
            className="p-2 rounded-lg hover:bg-white/15 transition-colors"
            title={t('common.close')}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 max-h-80 min-h-44 bg-gray-50">
        {messages.map((msg, idx) => (
          <div key={idx}>
            <div className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-primary-600 text-white rounded-br-md'
                    : 'bg-white border border-gray-200 text-gray-700 rounded-bl-md shadow-sm'
                }`}
              >
                {msg.text}
              </div>
            </div>
            {msg.showClearAction && idx === messages.length - 1 && (
              <button
                onClick={handleClear}
                className="mt-2 text-xs text-primary-600 hover:text-primary-700 hover:underline font-medium"
              >
                {t('shopAssistant.showAll')}
              </button>
            )}
          </div>
        ))}
        {searching && (
          <div className="flex justify-start">
            <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-md px-3 py-2 shadow-sm">
              <Loader2 className="w-4 h-4 animate-spin text-primary-600" />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="flex items-center gap-2 px-3 py-3 border-t border-gray-200 bg-white">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSend()
          }}
          placeholder={t('shopAssistant.placeholder')}
          disabled={searching}
          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none disabled:bg-gray-50"
        />
        {voiceSupported && (
          <button
            onClick={() => (isListening ? stopListening() : startListening())}
            className={`p-2 rounded-lg transition-colors ${
              isListening
                ? 'bg-red-100 text-red-600 animate-pulse'
                : 'text-gray-500 hover:bg-gray-100 hover:text-primary-600'
            }`}
            title={isListening ? t('shopAssistant.stopListening') : t('shopAssistant.speak')}
          >
            {isListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
          </button>
        )}
        <button
          onClick={() => handleSend()}
          disabled={!inputValue.trim() || searching}
          className="p-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          title={t('shopAssistant.send')}
        >
          <Send className="w-5 h-5" />
        </button>
      </div>
    </div>
  )
}
